import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, Chip, Grid, Card, CardContent,
    CardActions, Divider, Alert, CircularProgress, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, IconButton, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    ToggleButton, ToggleButtonGroup, InputAdornment, Tooltip, Select,
    FormControl, InputLabel, Checkbox, FormControlLabel
} from '@mui/material';
import {
    Upload as UploadIcon, Delete as DeleteIcon,
    Add as AddIcon, ArrowBack as ArrowBackIcon,
    Compare as CompareIcon, FilterList as FilterIcon,
    Close as CloseIcon, Download as DownloadIcon,
    Edit as EditIcon,
    Restore as RestoreIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { siteAPI } from '../services/api';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const VendorComparisonPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isManager = user?.role === 'manager';
    const canEdit = isAdmin || isManager;

    const siteIdParam = searchParams.get('siteId');
    const siteNameParam = searchParams.get('siteName');

    const [sites, setSites] = useState([]);
    const [selectedSiteId, setSelectedSiteId] = useState(siteIdParam || '');
    const [comparisons, setComparisons] = useState([]);
    const [selectedComparison, setSelectedComparison] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [activeTab, setActiveTab] = useState(0); // 0=pivot, 1=raw
    const [editQuoteOpen, setEditQuoteOpen] = useState(false);
    const [editQuote, setEditQuote] = useState(null);
    const [showTrash, setShowTrash] = useState(false);
    const [trashedQuotes, setTrashedQuotes] = useState([]);

    // Upload state
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadNotes, setUploadNotes] = useState('');
    const [uploading, setUploading] = useState(false);

    // Manual entry state
    const [manualOpen, setManualOpen] = useState(false);
    const [manualRows, setManualRows] = useState([{
        vendorName: '', vendorMobile: '', location: '', material: '',
        make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: ''
    }]);

    // Filters
    const [filterVendor, setFilterVendor] = useState('');
    const [filterMaterial, setFilterMaterial] = useState('');
    const [filterMake, setFilterMake] = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [filterBillType, setFilterBillType] = useState('');

    const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

    const fetchSites = useCallback(async () => {
        try { const res = await siteAPI.getAll(); setSites(res.data.data.sites); } catch (e) { console.error(e); }
    }, []);

    const fetchComparisons = useCallback(async (siteId) => {
        if (!siteId) return;
        setLoading(true);
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/site/${siteId}`, authHeaders());
            const list = res.data.data.comparisons;
            setComparisons(list);
            if (list.length > 0) fetchComparisonDetail(list[0].id);
        } catch (e) { setError('Failed to load comparisons'); }
        finally { setLoading(false); }
    }, []);

    const fetchComparisonDetail = async (id) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/${id}`, authHeaders());
            setSelectedComparison(res.data.data.comparison);
        } catch (e) { setError('Failed to load comparison details'); }
    };

    useEffect(() => { fetchSites(); }, [fetchSites]);
    useEffect(() => { if (selectedSiteId) fetchComparisons(selectedSiteId); }, [selectedSiteId, fetchComparisons]);

    const handleUpload = async () => {
        if (!uploadFile || !selectedSiteId) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('comparisonFile', uploadFile);
            formData.append('siteId', selectedSiteId);
            if (uploadNotes) formData.append('notes', uploadNotes);
            const res = await axios.post(`${API_BASE_URL}/vendor-comparisons/upload`, formData, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'multipart/form-data' }
            });
            setSuccess(`${res.data.data.comparison.versionLabel} uploaded!`);
            setUploadOpen(false); setUploadFile(null); setUploadNotes('');
            fetchComparisons(selectedSiteId);
        } catch (e) { setError(e.response?.data?.message || 'Upload failed'); }
        finally { setUploading(false); }
    };

    const handleDownloadTemplate = () => {
        const XLSX = require('xlsx');
        const templateData = [
            {
                'Vender Name': 'Aman',
                'Vender Mobile No.': '9958599023',
                'Location': 'Dwarka_Delhi',
                'Material': 'TMT Bar 16MM',
                'Make': 'JSW',
                'Unit': 'KG',
                'Qty': 1,
                'Rate': 58,
                'GST Bill /Non GST Bill': 'GST Bill',
                'Remarks': ''
            },
            {
                'Vender Name': 'Sakshi',
                'Vender Mobile No.': '9625166309',
                'Location': 'GZB_UP',
                'Material': 'TMT Bar 16MM',
                'Make': 'Kamdhenu',
                'Unit': 'KG',
                'Qty': 1,
                'Rate': 63,
                'GST Bill /Non GST Bill': 'Non GST Bill',
                'Remarks': ''
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Vendor Comparison');
        XLSX.writeFile(wb, 'vendor_comparison_template.xlsx');
    };

    const handleAddManual = async () => {
        const validRows = manualRows.filter(r => r.vendorName && r.material);
        if (validRows.length === 0) { setError('At least one row with Vendor Name and Material is required'); return; }
        try {
            if (!selectedComparison) {
                // Create new comparison first
                const existingCount = comparisons.length;
                const res = await axios.post(`${API_BASE_URL}/vendor-comparisons/upload`,
                    { siteId: selectedSiteId, notes: 'Manual entry' },
                    authHeaders()
                );
                await axios.post(`${API_BASE_URL}/vendor-comparisons/${res.data.data.comparison.id}/quotes`,
                    { quotes: validRows }, authHeaders());
                setSuccess('Quotes added!');
                fetchComparisons(selectedSiteId);
            } else {
                await axios.post(`${API_BASE_URL}/vendor-comparisons/${selectedComparison.id}/quotes`,
                    { quotes: validRows }, authHeaders());
                setSuccess('Quotes added!');
                fetchComparisonDetail(selectedComparison.id);
            }
            setManualOpen(false);
            setManualRows([{ vendorName: '', vendorMobile: '', location: '', material: '', make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: '' }]);
        } catch (e) { setError('Failed to add quotes'); }
    };

    const handleDeleteComparison = async (id) => {
        if (!window.confirm('Delete this comparison? This cannot be undone.')) return;
        try {
            await axios.delete(`${API_BASE_URL}/vendor-comparisons/${id}`, authHeaders());
            setSuccess('Comparison deleted');
            setSelectedComparison(null);
            fetchComparisons(selectedSiteId);
        } catch (e) { setError('Failed to delete'); }
    };

    const fetchTrashedQuotes = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/vendor-comparisons/trash?siteId=${selectedSiteId}`, authHeaders());
            setTrashedQuotes(res.data.data.quotes || []);
        } catch (e) { setError('Failed to fetch trash'); }
    };

    const handleSoftDelete = async (quoteId) => {
        if (!window.confirm('Move this quote to trash?')) return;
        try {
            await axios.delete(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/soft`, authHeaders());
            setSuccess('Quote moved to trash');
            fetchComparisonDetail(selectedComparison.id);
            if (showTrash) fetchTrashedQuotes();
        } catch (e) { setError('Failed to delete quote'); }
    };

    const handleHardDelete = async (quoteId) => {
        if (!window.confirm('Permanently delete this quote? Cannot be recovered.')) return;
        try {
            await axios.delete(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/hard`, authHeaders());
            setSuccess('Quote permanently deleted');
            fetchTrashedQuotes();
        } catch (e) { setError('Failed to delete quote'); }
    };

    const handleRestoreQuote = async (quoteId) => {
        try {
            await axios.put(`${API_BASE_URL}/vendor-comparisons/quotes/${quoteId}/restore`, {}, authHeaders());
            setSuccess('Quote restored');
            fetchTrashedQuotes();
            fetchComparisonDetail(selectedComparison.id);
        } catch (e) { setError('Failed to restore quote'); }
    };

    const handleUpdateQuote = async () => {
        try {
            await axios.put(`${API_BASE_URL}/vendor-comparisons/quotes/${editQuote.id}`, editQuote, authHeaders());
            setSuccess('Quote updated');
            setEditQuoteOpen(false);
            setSelectedComparison(prev => ({
                ...prev,
                quotes: prev.quotes.map(q => q.id === editQuote.id ? { ...q, ...editQuote } : q)
            }));
        } catch (e) { setError('Failed to update quote'); }
    };

    // Build pivot table data
    const buildPivotData = () => {
        if (!selectedComparison?.quotes) return { materials: [], vendors: [], pivotMap: {} };

        let quotes = selectedComparison.quotes;

        // Apply filters
        if (filterVendor) quotes = quotes.filter(q => q.vendorName.toLowerCase().includes(filterVendor.toLowerCase()));
        if (filterMaterial) quotes = quotes.filter(q => q.material.toLowerCase().includes(filterMaterial.toLowerCase()));
        if (filterMake) quotes = quotes.filter(q => (q.make || '').toLowerCase().includes(filterMake.toLowerCase()));
        if (filterLocation) quotes = quotes.filter(q => (q.location || '').toLowerCase().includes(filterLocation.toLowerCase()));
        if (filterBillType) quotes = quotes.filter(q => q.billType === filterBillType);

        const materials = [...new Set(quotes.map(q => q.material))].sort();
        const vendors = [...new Set(quotes.map(q => q.vendorName))].sort();

        // pivotMap[material][vendorName] = array of quotes
        const pivotMap = {};
        quotes.forEach(q => {
            if (!pivotMap[q.material]) pivotMap[q.material] = {};
            if (!pivotMap[q.material][q.vendorName]) pivotMap[q.material][q.vendorName] = [];
            pivotMap[q.material][q.vendorName].push(q);
        });

        return { materials, vendors, pivotMap };
    };

    const { materials, vendors, pivotMap } = buildPivotData();
    const selectedSiteName = siteNameParam || sites.find(s => s.id === selectedSiteId)?.siteName || '';

    // Vendor colors for columns
    const vendorColors = ['#e8f5e9', '#e3f2fd', '#fff3e0', '#fce4ec', '#f3e5f5', '#e0f7fa', '#f9fbe7'];

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate('/sites')} size="small"><ArrowBackIcon /></IconButton>
                    <Box>
                        <Typography variant="h4">Vendor Comparison</Typography>
                        {selectedSiteName && <Typography variant="body2" color="text.secondary">{selectedSiteName}</Typography>}
                    </Box>
                </Box>
                {canEdit && selectedSiteId && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setManualOpen(true)}>Add Manual</Button>
                        <Button variant="contained" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)}>Upload Excel</Button>
                        {isAdmin && (
                            <Button variant="outlined" color={showTrash ? 'error' : 'inherit'}
                                onClick={() => { setShowTrash(!showTrash); if (!showTrash) fetchTrashedQuotes(); }}>
                                {showTrash ? 'Hide Trash' : 'Trash'}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            {/* Site Selector */}
            {!siteIdParam && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <TextField select fullWidth label="Select Site" value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)}>
                        {sites.map(s => <MenuItem key={s.id} value={s.id}>{s.siteName} — {s.location}</MenuItem>)}
                    </TextField>
                </Paper>
            )}

            {!selectedSiteId && <Alert severity="info">Please select a site to view vendor comparisons.</Alert>}
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}

            {!loading && selectedSiteId && (
                <Grid container spacing={2}>
                    {/* Left: Version list */}
                    <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Versions</Typography>
                            {comparisons.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No comparisons yet</Typography>
                            ) : (
                                comparisons.map(c => (
                                    <Box key={c.id} sx={{
                                        p: 1.5, mb: 1, borderRadius: 1, cursor: 'pointer',
                                        border: '1px solid',
                                        borderColor: selectedComparison?.id === c.id ? 'primary.main' : 'divider',
                                        bgcolor: selectedComparison?.id === c.id ? 'primary.50' : 'background.paper',
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }} onClick={() => fetchComparisonDetail(c.id)}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="body2" fontWeight={600}>{c.versionLabel}</Typography>
                                            {canEdit && (
                                                <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteComparison(c.id); }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </Box>
                                        <Typography variant="caption" color="text.secondary">
                                            {c.quotes?.length || 0} quotes • {c.createdAt || c.created_at ? new Date(c.createdAt || c.created_at).toLocaleDateString('en-IN') : new Date().toLocaleDateString('en-IN')}
                                        </Typography>
                                        {c.notes && <Typography variant="caption" color="text.secondary" display="block">📝 {c.notes}</Typography>}
                                    </Box>
                                ))
                            )}
                        </Paper>
                    </Grid>

                    {/* Right: Pivot table */}
                    <Grid item xs={12} md={9}>
                        {!selectedComparison ? (
                            <Paper sx={{ p: 6, textAlign: 'center' }}>
                                <CompareIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">Select a version to view comparison</Typography>
                            </Paper>
                        ) : (
                            <Paper sx={{ p: 0 }}>
                                {/* Tabs */}
                                <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                                        <Tab label="Pivot View" />
                                        <Tab label="Raw Data" />
                                    </Tabs>
                                </Box>

                                {/* Filters */}
                                <Box sx={{ p: 2, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                                    <TextField size="small" label="Vendor" value={filterVendor} onChange={e => setFilterVendor(e.target.value)} sx={{ width: 140 }} />
                                    <TextField size="small" label="Material" value={filterMaterial} onChange={e => setFilterMaterial(e.target.value)} sx={{ width: 140 }} />
                                    <TextField size="small" label="Make" value={filterMake} onChange={e => setFilterMake(e.target.value)} sx={{ width: 120 }} />
                                    <TextField size="small" label="Location" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} sx={{ width: 130 }} />
                                    <TextField size="small" select label="Bill Type" value={filterBillType} onChange={e => setFilterBillType(e.target.value)} sx={{ width: 130 }}>
                                        <MenuItem value="">All</MenuItem>
                                        <MenuItem value="GST Bill">GST Bill</MenuItem>
                                        <MenuItem value="Non GST Bill">Non GST Bill</MenuItem>
                                    </TextField>
                                    {(filterVendor || filterMaterial || filterMake || filterLocation || filterBillType) && (
                                        <Button size="small" onClick={() => { setFilterVendor(''); setFilterMaterial(''); setFilterMake(''); setFilterLocation(''); setFilterBillType(''); }}>
                                            Clear Filters
                                        </Button>
                                    )}
                                </Box>

                                {/* Pivot Table */}
                                {activeTab === 0 && (
                                    <Box sx={{ overflow: 'auto', maxHeight: '65vh' }}>
                                        {materials.length === 0 ? (
                                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                                <Typography color="text.secondary">No data matches the filters</Typography>
                                            </Box>
                                        ) : (
                                            <table style={{ borderCollapse: 'collapse', minWidth: 'max-content', width: '100%', fontSize: '12px', fontFamily: 'Calibri, Arial, sans-serif' }}>
                                                <thead>
                                                    {/* Row 1: Vendor names spanning their makes */}
                                                    <tr>
                                                        <th style={{ ...thStyle, minWidth: 120, position: 'sticky', left: 0, zIndex: 3, background: '#1a237e', color: 'white' }}>Material</th>
                                                        <th style={{ ...thStyle, minWidth: 60, position: 'sticky', left: 120, zIndex: 3, background: '#1a237e', color: 'white' }}>Unit</th>
                                                        <th style={{ ...thStyle, minWidth: 60, position: 'sticky', left: 180, zIndex: 3, background: '#1a237e', color: 'white' }}>Qty</th>
                                                        {vendors.map((vendor, vIdx) => {
                                                            // Count unique makes for this vendor
                                                            const makes = [...new Set(
                                                                (selectedComparison?.quotes || [])
                                                                    .filter(q => q.vendorName === vendor)
                                                                    .map(q => q.make || 'NA')
                                                            )];
                                                            return (
                                                                <th key={vendor} colSpan={makes.length}
                                                                    style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#1a237e', borderBottom: '2px solid #1a237e', minWidth: 100 }}>
                                                                    {vendor}
                                                                </th>
                                                            );
                                                        })}
                                                        <th style={{ ...thStyle, background: '#ffd700', color: '#1a237e', minWidth: 100 }}>Grand Total</th>
                                                    </tr>
                                                    {/* Row 1b: Phone + Location under vendor name */}
                                                    <tr>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '10px' }}>—</th>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 120, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '10px' }}>—</th>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 180, zIndex: 3, background: '#1a237e', color: 'white', fontSize: '10px' }}>—</th>
                                                        {vendors.map((vendor, vIdx) => {
                                                            const makes = [...new Set(
                                                                (selectedComparison?.quotes || [])
                                                                    .filter(q => q.vendorName === vendor)
                                                                    .map(q => q.make || 'NA')
                                                            )];
                                                            const vendorQuote = (selectedComparison?.quotes || []).find(q => q.vendorName === vendor);
                                                            return (
                                                                <th key={vendor} colSpan={makes.length}
                                                                    style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#555', fontSize: '10px' }}>
                                                                    {vendorQuote?.vendorMobile || ''} {vendorQuote?.location ? `• ${vendorQuote.location}` : ''}
                                                                </th>
                                                            );
                                                        })}
                                                        <th style={{ ...thStyle, background: '#ffc107', fontSize: '10px' }}>—</th>
                                                    </tr>
                                                    {/* Row 2: Make names under each vendor */}
                                                    <tr>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 0, zIndex: 3, background: '#283593', color: 'white', fontSize: '10px' }}>—</th>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 120, zIndex: 3, background: '#283593', color: 'white', fontSize: '10px' }}>—</th>
                                                        <th style={{ ...thStyle, position: 'sticky', left: 180, zIndex: 3, background: '#283593', color: 'white', fontSize: '10px' }}>—</th>
                                                        {vendors.map((vendor, vIdx) => {
                                                            const makes = [...new Set(
                                                                (selectedComparison?.quotes || [])
                                                                    .filter(q => q.vendorName === vendor)
                                                                    .map(q => q.make || 'NA')
                                                            )];
                                                            return makes.map(make => (
                                                                <th key={`${vendor}-${make}`}
                                                                    style={{ ...thStyle, background: vendorColors[vIdx % vendorColors.length], color: '#555', fontSize: '10px', minWidth: 80 }}>
                                                                    {make}
                                                                </th>
                                                            ));
                                                        })}
                                                        <th style={{ ...thStyle, background: '#ffc107', color: '#1a237e', fontSize: '10px' }}>Sum</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {materials.map((material, mIdx) => {
                                                        // Get unit and qty from first quote for this material
                                                        const firstQuote = selectedComparison.quotes.find(q => q.material === material);
                                                        const rowBg = mIdx % 2 === 0 ? 'white' : '#f9f9f9';
                                                        let grandTotal = 0;

                                                        return (
                                                            <tr key={material}>
                                                                <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#e8eaf6', fontWeight: 600, zIndex: 1 }}>{material}</td>
                                                                <td style={{ ...tdStyle, position: 'sticky', left: 120, background: '#e8eaf6', zIndex: 1 }}>{firstQuote?.unit || ''}</td>
                                                                <td style={{ ...tdStyle, position: 'sticky', left: 180, background: '#e8eaf6', zIndex: 1 }}>{firstQuote?.qty || ''}</td>
                                                                {vendors.map((vendor, vIdx) => {
                                                                    const makes = [...new Set(
                                                                        (selectedComparison?.quotes || [])
                                                                            .filter(q => q.vendorName === vendor)
                                                                            .map(q => q.make || 'NA')
                                                                    )];
                                                                    return makes.map(make => {
                                                                        const quote = (pivotMap[material]?.[vendor] || []).find(q => (q.make || 'NA') === make);
                                                                        const rate = quote ? parseFloat(quote.rate) : null;
                                                                        if (rate) grandTotal += rate;
                                                                        return (
                                                                            <td key={`${vendor}-${make}`} style={{
                                                                                ...tdStyle,
                                                                                background: rate ? vendorColors[vIdx % vendorColors.length] : rowBg,
                                                                                textAlign: 'right',
                                                                                fontWeight: rate ? 600 : 400,
                                                                                color: rate ? '#1a237e' : '#999'
                                                                            }}>
                                                                                {rate ? rate.toLocaleString('en-IN') : '—'}
                                                                            </td>
                                                                        );
                                                                    });
                                                                })}
                                                                <td style={{ ...tdStyle, background: '#fff9c4', textAlign: 'right', fontWeight: 700, color: '#e65100' }}>
                                                                    {grandTotal > 0 ? grandTotal.toLocaleString('en-IN') : '—'}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                    {/* Grand Total Row */}
                                                    <tr>
                                                        <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#ffd700', fontWeight: 700, zIndex: 1 }}>Grand Total</td>
                                                        <td style={{ ...tdStyle, position: 'sticky', left: 120, background: '#ffd700', zIndex: 1 }}></td>
                                                        <td style={{ ...tdStyle, position: 'sticky', left: 180, background: '#ffd700', zIndex: 1 }}></td>
                                                        {vendors.map((vendor, vIdx) => {
                                                            const makes = [...new Set(
                                                                (selectedComparison?.quotes || [])
                                                                    .filter(q => q.vendorName === vendor)
                                                                    .map(q => q.make || 'NA')
                                                            )];
                                                            return makes.map(make => {
                                                                const total = materials.reduce((sum, material) => {
                                                                    const quote = (pivotMap[material]?.[vendor] || []).find(q => (q.make || 'NA') === make);
                                                                    return sum + (quote ? parseFloat(quote.rate) || 0 : 0);
                                                                }, 0);
                                                                return (
                                                                    <td key={`total-${vendor}-${make}`} style={{ ...tdStyle, background: '#ffd700', textAlign: 'right', fontWeight: 700 }}>
                                                                        {total > 0 ? total.toLocaleString('en-IN') : '—'}
                                                                    </td>
                                                                );
                                                            });
                                                        })}
                                                        <td style={{ ...tdStyle, background: '#ffc107', textAlign: 'right', fontWeight: 700 }}>
                                                            {materials.reduce((sum, material) => {
                                                                return sum + vendors.reduce((vSum, vendor) => {
                                                                    const makes = [...new Set((selectedComparison?.quotes || []).filter(q => q.vendorName === vendor).map(q => q.make || 'NA'))];
                                                                    return vSum + makes.reduce((mSum, make) => {
                                                                        const quote = (pivotMap[material]?.[vendor] || []).find(q => (q.make || 'NA') === make);
                                                                        return mSum + (quote ? parseFloat(quote.rate) || 0 : 0);
                                                                    }, 0);
                                                                }, 0);
                                                            }, 0).toLocaleString('en-IN')}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        )}
                                    </Box>
                                )}

                                {/* Raw Data Tab */}
                                {activeTab === 1 && (
                                    <Box sx={{ overflow: 'auto', maxHeight: '65vh' }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#1a237e' }}>
                                                    {['Vendor', 'Mobile', 'Location', 'Material', 'Make', 'Unit', 'Qty', 'Rate', 'Bill Type', 'Remarks'].map(h => (
                                                        <TableCell key={h} sx={{ fontWeight: 700, bgcolor: '#1a237e', color: 'white', fontSize: '11px' }}>{h}</TableCell>
                                                    ))}
                                                    {canEdit && <TableCell sx={{ bgcolor: '#1a237e', color: 'white' }}>Actions</TableCell>}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {(selectedComparison.quotes || []).map((q, idx) => (
                                                    <TableRow key={q.id} hover sx={{ bgcolor: idx % 2 === 0 ? 'white' : '#f9f9f9' }}>
                                                        <TableCell sx={{ fontSize: '11px', fontWeight: 600 }}>{q.vendorName}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.vendorMobile}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.location}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.material}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.make}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.unit}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px', textAlign: 'right' }}>{q.qty}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px', textAlign: 'right', fontWeight: 600 }}>{q.rate}</TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>
                                                            <Chip label={q.billType} size="small" color={q.billType === 'GST' ? 'success' : 'default'} />
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: '11px' }}>{q.remarks}</TableCell>
                                                        {canEdit && (
                                                            <TableCell>
                                                                <Tooltip title="Edit">
                                                                    <IconButton size="small" color="primary" onClick={() => { setEditQuote({ ...q }); setEditQuoteOpen(true); }}>
                                                                        <VisibilityIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Move to Trash">
                                                                    <IconButton size="small" color="warning" onClick={() => handleSoftDelete(q.id)}>
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                                {isAdmin && (
                                                                    <Tooltip title="Permanent Delete">
                                                                        <IconButton size="small" color="error" onClick={() => handleHardDelete(q.id)}>
                                                                            <DeleteIcon fontSize="small" sx={{ opacity: 0.6 }} />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                            </TableCell>
                                                        )}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </Box>
                                )}
                            </Paper>
                        )}
                    </Grid>
                </Grid>
            )}

            {/* Edit Quote Dialog */}
            <Dialog open={editQuoteOpen} onClose={() => setEditQuoteOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Quote</DialogTitle>
                <DialogContent>
                    {editQuote && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <TextField fullWidth label="Vendor Name" value={editQuote.vendorName || ''} onChange={e => setEditQuote({ ...editQuote, vendorName: e.target.value })} />
                            <TextField fullWidth label="Mobile" value={editQuote.vendorMobile || ''} onChange={e => setEditQuote({ ...editQuote, vendorMobile: e.target.value })} />
                            <TextField fullWidth label="Location" value={editQuote.location || ''} onChange={e => setEditQuote({ ...editQuote, location: e.target.value })} />
                            <TextField fullWidth label="Material" value={editQuote.material || ''} onChange={e => setEditQuote({ ...editQuote, material: e.target.value })} />
                            <TextField fullWidth label="Make" value={editQuote.make || ''} onChange={e => setEditQuote({ ...editQuote, make: e.target.value })} />
                            <TextField fullWidth label="Unit" value={editQuote.unit || ''} onChange={e => setEditQuote({ ...editQuote, unit: e.target.value })} />
                            <TextField fullWidth type="number" label="Qty" value={editQuote.qty || ''} onChange={e => setEditQuote({ ...editQuote, qty: e.target.value })} />
                            <TextField fullWidth type="number" label="Rate" value={editQuote.rate || ''} onChange={e => setEditQuote({ ...editQuote, rate: e.target.value })} />
                            <TextField fullWidth select label="Bill Type" value={editQuote.billType || 'GST Bill'} onChange={e => setEditQuote({ ...editQuote, billType: e.target.value })}>
                                <MenuItem value="GST Bill">GST Bill</MenuItem>
                                <MenuItem value="Non GST Bill">Non GST Bill</MenuItem>
                            </TextField>
                            <TextField fullWidth label="Remarks" value={editQuote.remarks || ''} onChange={e => setEditQuote({ ...editQuote, remarks: e.target.value })} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditQuoteOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpdateQuote}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* Trash Table */}
            {showTrash && isAdmin && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" color="error" gutterBottom>🗑️ Deleted Quotes (Trash)</Typography>
                    <TableContainer component={Paper} sx={{ border: '2px solid #ffcdd2' }}>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: '#ffebee' }}>
                                <TableRow>
                                    {['Vendor', 'Material', 'Make', 'Rate', 'Bill Type', 'Deleted At', 'Actions'].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: '11px' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trashedQuotes.length === 0 ? (
                                    <TableRow><TableCell colSpan={7} align="center">No deleted quotes</TableCell></TableRow>
                                ) : (
                                    trashedQuotes.map(q => (
                                        <TableRow key={q.id} sx={{ bgcolor: '#fff8f8' }}>
                                            <TableCell sx={{ fontSize: '11px', fontWeight: 600 }}>{q.vendorName}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.material}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.make}</TableCell>
                                            <TableCell sx={{ fontSize: '11px', textAlign: 'right' }}>{q.rate}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{q.billType}</TableCell>
                                            <TableCell sx={{ fontSize: '11px' }}>{new Date(q.deletedAt).toLocaleString('en-IN')}</TableCell>
                                            <TableCell>
                                                <Tooltip title="Restore">
                                                    <IconButton size="small" color="success" onClick={() => handleRestoreQuote(q.id)}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Forever">
                                                    <IconButton size="small" color="error" onClick={() => handleHardDelete(q.id)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Upload Dialog */}
            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Vendor Comparison Excel</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Excel columns: Vender Name, Vender Mobile No., Location, Material, Make, Unit, Qty, Rate, GST Bill /Non GST Bill, Remarks
                    </Alert>
                    <Button variant="outlined" fullWidth startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ mb: 2 }}>
                        Download Template
                    </Button>
                    <Button variant="outlined" component="label" fullWidth startIcon={<UploadIcon />} sx={{ mb: 2 }}>
                        {uploadFile ? uploadFile.name : 'Select Excel File (.xlsx, .xls)'}
                        <input type="file" hidden accept=".xlsx,.xls" onChange={(e) => setUploadFile(e.target.files[0])} />
                    </Button>
                    <TextField fullWidth multiline rows={2} label="Notes (Optional)" value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpload} disabled={!uploadFile || uploading}
                        startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}>
                        {uploading ? 'Uploading...' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Manual Entry Dialog */}
            <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="xl" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Add Vendor Quotes Manually</Typography>
                        <IconButton onClick={() => setManualOpen(false)}><CloseIcon /></IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ overflow: 'auto' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '12px' }}>
                            <thead>
                                <tr>
                                    {['Vendor Name *', 'Mobile', 'Location', 'Material *', 'Make', 'Unit', 'Qty', 'Rate', 'Bill Type', 'Remarks', ''].map(h => (
                                        <th key={h} style={{ ...thStyle, background: '#1a237e', color: 'white', padding: '8px' }}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {manualRows.map((row, idx) => (
                                    <tr key={idx}>
                                        {['vendorName', 'vendorMobile', 'location', 'material', 'make', 'unit', 'qty', 'rate'].map(field => (
                                            <td key={field} style={{ padding: '4px' }}>
                                                <input
                                                    value={row[field]}
                                                    onChange={(e) => {
                                                        const updated = [...manualRows];
                                                        updated[idx][field] = e.target.value;
                                                        setManualRows(updated);
                                                    }}
                                                    style={{ width: field === 'vendorName' || field === 'material' ? 140 : field === 'location' ? 120 : 80, padding: '4px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }}
                                                    placeholder={field === 'vendorName' ? 'Required' : field === 'material' ? 'Required' : ''}
                                                />
                                            </td>
                                        ))}
                                        <td style={{ padding: '4px' }}>
                                            <select value={row.billType} onChange={(e) => { const u = [...manualRows]; u[idx].billType = e.target.value; setManualRows(u); }}
                                                style={{ padding: '4px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }}>
                                                <option value="GST">GST Bill</option>
                                                <option value="Non GST Bill">Non GST Bill</option>
                                            </select>
                                        </td>
                                        <td style={{ padding: '4px' }}>
                                            <input value={row.remarks} onChange={(e) => { const u = [...manualRows]; u[idx].remarks = e.target.value; setManualRows(u); }}
                                                style={{ width: 120, padding: '4px', border: '1px solid #ddd', borderRadius: 4, fontSize: '12px' }} />
                                        </td>
                                        <td style={{ padding: '4px' }}>
                                            <IconButton size="small" color="error" onClick={() => setManualRows(manualRows.filter((_, i) => i !== idx))}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </Box>
                    <Button startIcon={<AddIcon />} onClick={() => setManualRows([...manualRows, { vendorName: '', vendorMobile: '', location: '', material: '', make: '', unit: '', qty: '', rate: '', billType: 'GST', remarks: '' }])}
                        sx={{ mt: 2 }}>
                        Add Row
                    </Button>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setManualOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleAddManual}>Save Quotes</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Styles
const thStyle = {
    border: '1px solid #ddd',
    padding: '6px 8px',
    textAlign: 'center',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    fontSize: '11px',
};

const tdStyle = {
    border: '1px solid #e0e0e0',
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    fontSize: '11px',
};

export default VendorComparisonPage;